import { Currency, type SuretyAccountId } from "@inrev/common";
import { useAdminUpdateBLOCSize } from "@portal/api/financial-period/admin";
import { useCallback, useEffect, useState } from "react";

type EditableBLOCSizeState =
	| { kind: "display"; size: number | null }
	| { kind: "editing"; updated: number | null }
	| { kind: "submitting"; updated: number };

type Options = {
	accountId: SuretyAccountId;
	blocSize?: number | null;
};

export type UseEditableBLOCSizeResult = {
	displayBLOCSize: number | null;
	updatedBLOCSize: number | null;
	isEditing: boolean;
	mutationError: Error | null;
	startEditing: () => void;
	cancelEditing: () => void;
	changeBLOCSize: (value: number | null) => void;
	submit: () => void;
	dismissError: () => void;
	canSubmit: boolean;
};

const toDisplayState = (blocSize?: number | null): EditableBLOCSizeState => ({
	kind: "display",
	size: blocSize ?? null,
});

export const useEditableBLOCSize = ({
	accountId,
	blocSize,
}: Options): UseEditableBLOCSizeResult => {
	const [state, setState] = useState<EditableBLOCSizeState>(() => toDisplayState(blocSize));

	useEffect(() => {
		setState((current) => (current.kind === "display" ? toDisplayState(blocSize) : current));
	}, [blocSize]);

	const { updateBLOCSize, updateBLOCSizeError, dismissUpdateBLOCSizeError } =
		useAdminUpdateBLOCSize(accountId);

	const startEditing = useCallback(() => {
		setState({ kind: "editing", updated: blocSize ?? null });
	}, [blocSize]);

	const cancelEditing = useCallback(() => {
		setState(toDisplayState(blocSize));
	}, [blocSize]);

	const changeBLOCSize = useCallback((value: number | null) => {
		setState((current) => (current.kind === "editing" ? { ...current, updated: value } : current));
	}, []);

	const submit = useCallback(() => {
		if (state.kind !== "editing" || state.updated === null) return;
		const blocSize = state.updated;

		setState({ kind: "submitting", updated: blocSize });

		updateBLOCSize({
			data: { blocSize: Currency.round(blocSize) },
			onSuccess: () => {
				setState({ kind: "display", size: blocSize });
			},
			onError: () => {
				setState({ kind: "editing", updated: blocSize });
			},
		});
	}, [state, updateBLOCSize]);

	const mutationError = updateBLOCSizeError ?? null;

	const dismissError = useCallback(() => {
		dismissUpdateBLOCSizeError();
	}, [dismissUpdateBLOCSizeError]);

	const canSubmit = state.kind === "editing" && state.updated !== null && state.updated >= 0;
	const isEditing = state.kind !== "display";
	const displayBLOCSize = state.kind === "display" ? state.size : null;
	const updatedBLOCSize =
		state.kind === "editing" || state.kind === "submitting" ? state.updated : null;

	return {
		displayBLOCSize,
		updatedBLOCSize,
		isEditing,
		mutationError,
		startEditing,
		cancelEditing,
		changeBLOCSize,
		submit,
		dismissError,
		canSubmit,
	};
};
